import { Menu, Transition } from '@headlessui/react';
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import Link from 'next/link';
import { Typography, TypographyType } from '../typography';
import { Option } from 'interfaces';
import {
	itemIsNotNullAndNotUndefined,
	itemIsNullOrUndefined,
} from 'utils/common';
import { Icon } from '../icon';
import { CypressI } from 'interfaces/cypress';
import { buildHeaderUserProfileOptions } from '@/utils';
import { headerUserProfileOptions } from '@/const';
import { useSession } from 'next-auth/react';

export interface DropdownProps {
	buttonContent: string | JSX.Element;
	showChevronDownIcon?: boolean;
	items?: Option[];
	customItems?: JSX.Element;
	disableHoverIndexes?: number[];
	// Styles
	className?: string;
	classNameButton?: string;
	classNameMenuItems?: string;
	classNameMenuItem?: string;
	classNameItem?: string;
	/** Extra class applied to the trigger button only while the menu is open — opt-in, appended after `classNameButton`. */
	classNameButtonActive?: string;
	customTypography?: TypographyType;
	isOpen?: boolean;
	setOpen?: Dispatch<SetStateAction<boolean>>;
}

export const Dropdown: React.FC<DropdownProps & CypressI> = ({
	showChevronDownIcon = true,
	isOpen,
	setOpen,
	// Styles
	className,
	classNameButton = 'w-auto rounded-md p-1 typ',
	classNameMenuItems = 'min-w-[210px] bg-white rounded-lg',
	classNameMenuItem = 'flex items-center gap-x-2 py-3 px-4 hover:bg-dark-10 rounded-lg',
	classNameItem = 'text-left text-base rounded-lg w-full',
	customTypography,
	...props
}): JSX.Element => {
	const session = useSession();
	const [userProfileOptions, setUserProfileOptions] = useState<Option[]>([]);

	// Build dropdown menu for user profile
	useEffect(() => {
		if (session != null) {
			if (props.items) {
				setUserProfileOptions(props.items);
			}
			if (!props.items) {
				const updatedOptions = buildHeaderUserProfileOptions(
					headerUserProfileOptions,
					session.status,
					session.data?.user.user.role
				);

				setUserProfileOptions(updatedOptions);
			}
		}
	}, [session, props.items]);

	return (
		<Menu data-cy={props.dataCY} as="div" className={clsx('w-fit', className)}>
			{({ open }) => (
				<>
					<Menu.Button
						data-cy={`${props.dataCY}-button`}
						onClick={() => setOpen?.((prev) => !prev)}
						className={clsx(
							'flex justify-center items-center',
							'focus:outline-none relative z-0',
							'focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-opacity-75',
							classNameButton,
							open && props.classNameButtonActive
						)}
					>
						{props.buttonContent}
						{showChevronDownIcon && (
							<ChevronDownIcon
								className="ml-1 -mr-1 h-6 w-6 text-[#292D32] hover:text-gray-700"
								aria-hidden="true"
							/>
						)}
					</Menu.Button>

					<Transition
						show={setOpen ? isOpen : open}
						as={Fragment}
						enter="transition ease-out duration-100"
						enterFrom="transform opacity-0 scale-95"
						enterTo="transform opacity-100 scale-100"
						leave="transition ease-in duration-75"
						leaveFrom="transform opacity-100 scale-100"
						leaveTo="transform opacity-0 scale-95"
					>
						<Menu.Items
							as="div"
							className={clsx(
								'absolute right-0 lg:left-auto lg:right-2 mt-1 z-[100] min-w-[100px]',
								'origin-top-right shadow-customShadow',
								'focus:outline-none',
								classNameMenuItems
							)}
						>
							{props.customItems != undefined && props.customItems != null && (
								<>{props.customItems}</>
							)}
							{(props.customItems == undefined || props.customItems == null) &&
								userProfileOptions?.map((item, index) => {
									const isHoverDisabled =
										props.disableHoverIndexes?.includes(index);
									const menuItemClass = clsx(
										classNameMenuItem,
										isHoverDisabled && 'hover:!bg-transparent !cursor-default'
									);

									return (
										<Menu.Item
											as="div"
											data-cy={`${props.dataCY}-option-${item.label}`}
											key={`dropDown-option-${item.label}-${index}`}
											className={item.className}
										>
											{/* If it's a link */}
											{item.href && itemIsNullOrUndefined(item.onClick) && (
												<Link href={item.href} className={menuItemClass}>
													{item.icon && (
														<Icon
															src={item.icon}
															className={clsx(
																'!size-5 text-primary',
																item.classNameItemIcon
															)}
														/>
													)}
													<Typography
														type={customTypography || 'subtitle-2'}
														className={clsx(classNameItem)}
													>
														{item.label}
													</Typography>
												</Link>
											)}

											{/* If it's a onClick */}
											{itemIsNotNullAndNotUndefined(item.onClick) &&
												itemIsNullOrUndefined(item.href) && (
													<>
														<button
															disabled={item.disabled}
															className={clsx(
																menuItemClass,
																classNameItem,
																item.disabled &&
																	'!cursor-not-allowed opacity-50 hover:!bg-transparent'
															)}
															onClick={() => {
																if (item.disabled) return;
																item.onClick && item.onClick(item);
															}}
														>
															{item.icon && (
																<Icon
																	src={item.icon}
																	className={clsx(
																		'!size-5 text-primary',
																		item.classNameItemIcon
																	)}
																/>
															)}
															<Typography
																type={customTypography || 'subtitle-2'}
																className={item.classNameItemLabel}
															>
																{item.label}
															</Typography>
														</button>
													</>
												)}
										</Menu.Item>
									);
								})}
						</Menu.Items>
					</Transition>
				</>
			)}
		</Menu>
	);
};
